import { Component } from '@theme/component';

/**
 * Custom element that powers the `tabbed-content` section.
 *
 * Each `_tab-panel` block renders a tab button followed by its panel inside
 * the panels container. On mount the buttons are relocated into the tab list
 * so the section's grid layout (sticky tabs + panels) is honoured.
 *
 * On tab change the component cross-fades panels and (on desktop only)
 * animates the panel wrapper height so the surrounding layout doesn't snap.
 * It also keeps `location.hash` in sync with the active panel and reacts to
 * `hashchange` events from history navigation or external links.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} tablist
 * @property {HTMLElement} panelsRoot
 * @property {HTMLButtonElement[]} tabs
 * @property {HTMLElement[]} panels
 *
 * @extends Component<Refs>
 */
class TabbedContent extends Component {
  requiredRefs = ['tablist', 'panelsRoot'];

  /** @type {number} */
  #activeIndex = -1;

  /** @type {AbortController | null} */
  #swapController = null;

  /** @type {MediaQueryList} */
  #mobileQuery = window.matchMedia('(max-width: 749px)');

  /** @type {MediaQueryList} */
  #reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.#relocateTabs();
    this.#relocatePanelAsides();

    super.connectedCallback();

    const { tabs } = this.refs;
    if (!tabs?.length) return;

    const mode = this.dataset.interactionMode;

    if (mode === 'scroll') {
      this.#setupScrollSpy();
      return;
    }

    // `scroll_mobile`: scroll-spy on mobile, classic tabs on desktop. Set up
    // whichever mode the current viewport calls for and re-resolve on breakpoint
    // crossings — tearing the old mode down and building the new one.
    if (mode === 'scroll_mobile') {
      this.#applyResponsiveMode();
      this.#mobileQuery.addEventListener('change', this.#handleModeChange);
      return;
    }

    this.#setupTabs();
  }

  /**
   * Wire up the classic tab widget: click/keyboard activation, hash sync, and
   * the initial activation. Listeners are bound to the lifecycle signal so a
   * single `abort()` removes them when switching modes or disconnecting.
   */
  #setupTabs() {
    const { tabs } = this.refs;
    const signal = this.#getLifecycleSignal();

    for (const tab of tabs) {
      tab.addEventListener('click', this.#handleClick, { signal });
      tab.addEventListener('keydown', this.#handleKeyDown, { signal });
    }

    window.addEventListener('hashchange', this.#handleHashChange, { signal });

    this.#activate(this.#resolveInitialIndex(), { updateHash: false, animate: false });
  }

  /**
   * `scroll_mobile` only: build the mode the current viewport needs.
   * Tracked so `#handleModeChange` can skip redundant rebuilds.
   *
   * @type {'tabs' | 'scroll' | null}
   */
  #activeResponsiveMode = null;

  #applyResponsiveMode() {
    const next = this.#mobileQuery.matches ? 'scroll' : 'tabs';
    if (next === this.#activeResponsiveMode) return;

    // Tear down the previous mode's listeners/observers before rebuilding.
    this.#lifecycleController?.abort();
    this.#lifecycleController = null;
    this.#swapController?.abort();
    this.#swapController = null;
    this.#activeIndex = -1;

    if (next === 'scroll') {
      this.#setupScrollSpy();
    } else {
      this.#resetToTabSemantics();
      this.#setupTabs();
    }

    this.#activeResponsiveMode = next;
  }

  /** Re-resolve the active mode when the mobile breakpoint is crossed. */
  #handleModeChange = () => this.#applyResponsiveMode();

  /**
   * Undo the DOM mutations `#setupScrollSpy` makes (region roles, jump-link
   * semantics, all-panels-visible) so the classic tab widget starts from a
   * clean slate when switching back to desktop tabs.
   */
  #resetToTabSemantics() {
    const { tabs, panels, tablist } = this.refs;

    for (const panel of panels) {
      panel.removeAttribute('role');
      panel.removeAttribute('aria-current');
      panel.removeAttribute('data-active');
      panel.setAttribute('hidden', '');
    }

    for (const tab of tabs) {
      tab.setAttribute('role', 'tab');
      tab.removeAttribute('aria-current');
    }

    if (tablist) {
      tablist.setAttribute('role', 'tablist');
      tablist.removeAttribute('aria-label');
      tablist.setAttribute(
        'aria-orientation',
        this.classList.contains('tabbed-content--tabs-left') ? 'vertical' : 'horizontal'
      );
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#swapController?.abort();
    this.#lifecycleController?.abort();
    this.#lifecycleController = null;
    this.#mobileQuery.removeEventListener('change', this.#handleModeChange);
  }

  /** @type {AbortController | null} */
  #lifecycleController = null;

  /**
   * AbortController that lives for the lifetime of this connection. We use it
   * to register every event listener so they can be cleaned up in a single
   * `abort()` call on disconnect.
   */
  #getLifecycleSignal() {
    this.#lifecycleController = new AbortController();
    return this.#lifecycleController.signal;
  }

  /**
   * Move tab buttons rendered inside the panels container up into the tab
   * list, then write the panel order numbers (01, 02, …) into both the tab
   * and eyebrow `<sup>` slots. We can't rely on CSS counters here because
   * inactive panels are `display: none`, which excludes them from counting.
   * Runs before `super.connectedCallback()` so the inherited ref scan picks
   * the relocated tabs up at their final location.
   */
  #relocateTabs() {
    const tablist = this.querySelector('[ref="tablist"]');
    const panelsRoot = this.querySelector('[ref="panelsRoot"]');

    if (!(tablist instanceof HTMLElement) || !(panelsRoot instanceof HTMLElement)) return;

    const tabs = Array.from(panelsRoot.querySelectorAll('[data-slot="tab"]'));
    const panels = Array.from(panelsRoot.querySelectorAll('.tabbed-content__panel'));

    tabs.forEach((tab, index) => {
      const ordinal = `(${String(index + 1).padStart(2, '0')})`;
      const tabNumber = tab.querySelector('.tabbed-content__tab-number');
      if (tabNumber) tabNumber.textContent = ordinal;

      const panel = panels[index];
      const eyebrowNumber = panel?.querySelector('.tabbed-content__eyebrow-number');
      if (eyebrowNumber) eyebrowNumber.textContent = ordinal;

      tablist.appendChild(tab);
    });
  }

  /**
   * Move any `arrow-link-list` block authored inside a panel-body up into the
   * panel's grid as a sibling of `.tabbed-content__panel-content`, so it
   * occupies the right (media) column instead of stacking under the body.
   * Panels that don't contain an arrow-link-list are left untouched.
   */
  #relocatePanelAsides() {
    const panels = this.querySelectorAll('.tabbed-content__panel');
    panels.forEach((panel) => {
      if (!panel.classList.contains('tabbed-content__panel--no-media')) return;
      const lists = panel.querySelectorAll(':scope > .tabbed-content__panel-content .arrow-link-list');
      // Only lift when there is a single list — multi-list bodies (e.g. Care
      // Guide with Jewellery + Watches sections) should stay in-flow.
      if (lists.length !== 1) return;
      const list = lists[0];
      panel.appendChild(list);
      list.classList.add('tabbed-content__panel-aside');
    });
  }

  /**
   * Resolve a raw URL hash to a tab index. Matches either the panel id
   * (`data-panel-id`) or the tab button's own id (`tab-<panelId>`), so deep
   * links pointing at either anchor resolve to the same panel.
   *
   * @param {string} hash - Hash without the leading `#`.
   * @returns {number} Tab index, or -1 when no panel matches.
   */
  #indexForHash(hash) {
    if (!hash) return -1;
    return this.refs.tabs.findIndex(
      (tab) => tab.dataset.panelId === hash || tab.id === hash
    );
  }

  /**
   * Pick the starting tab: URL hash takes precedence, then `data-default-index`.
   *
   * @returns {number}
   */
  #resolveInitialIndex() {
    const tabs = this.refs.tabs;
    const matched = this.#indexForHash(window.location.hash.replace(/^#/, ''));
    if (matched !== -1) return matched;

    const fallback = Number(this.dataset.defaultIndex ?? 0);
    if (!Number.isFinite(fallback)) return 0;
    return Math.min(Math.max(fallback, 0), tabs.length - 1);
  }

  /**
   * Activate the panel at `index`.
   *
   * @param {number} index
   * @param {object} [options]
   * @param {boolean} [options.focus] - Move keyboard focus to the activated tab.
   * @param {boolean} [options.updateHash] - Update `location.hash` with the panel id.
   * @param {boolean} [options.animate] - Animate the transition (false on first paint).
   */
  #activate(index, { focus = false, updateHash = true, animate = true } = {}) {
    if (index === this.#activeIndex) return;

    const { tabs, panels, panelsRoot } = this.refs;

    tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.setAttribute('tabindex', active ? '0' : '-1');
    });

    const previousPanel = this.#activeIndex >= 0 ? panels[this.#activeIndex] : null;
    const nextPanel = panels[index];
    if (!nextPanel) return;

    this.#activeIndex = index;

    if (focus) tabs[index].focus();

    if (updateHash) {
      const panelId = tabs[index].dataset.panelId;
      if (panelId) history.replaceState(null, '', `#${panelId}`);
    }

    this.#ensureTabVisible(tabs[index], { instant: !animate });

    const skipChoreography = !animate || !previousPanel || this.#reducedMotionQuery.matches;

    if (skipChoreography) {
      previousPanel?.removeAttribute('data-active');
      previousPanel?.setAttribute('hidden', '');
      nextPanel.removeAttribute('hidden');
      // Force a layout pass before setting data-active so the entrance
      // transition replays even on the first render.
      requestAnimationFrame(() => nextPanel.setAttribute('data-active', ''));
      return;
    }

    void this.#animateSwap(previousPanel, nextPanel, panelsRoot);
  }

  /**
   * Centre the active tab within its scroll container when the tab list
   * overflows (mobile). No-op when the row already fits. Uses `block: 'nearest'`
   * so the surrounding page never scrolls vertically.
   *
   * @param {HTMLElement} tab
   * @param {{ instant?: boolean }} [options]
   */
  #ensureTabVisible(tab, { instant = false } = {}) {
    const container = this.refs.tablist;
    if (!(container instanceof HTMLElement)) return;
    if (container.scrollWidth <= container.clientWidth) return;

    const behavior = instant || this.#reducedMotionQuery.matches ? 'auto' : 'smooth';

    tab.scrollIntoView({
      inline: 'center',
      block: 'nearest',
      behavior,
    });
  }

  /**
   * Cross-fade the previous panel out, fade the next one in, and (on desktop)
   * animate the panel wrapper height to fit the new content.
   *
   * @param {HTMLElement} prev
   * @param {HTMLElement} next
   * @param {HTMLElement} wrapper
   */
  async #animateSwap(prev, next, wrapper) {
    this.#swapController?.abort();
    const controller = new AbortController();
    this.#swapController = controller;
    const { signal } = controller;

    const animateHeight = !this.#mobileQuery.matches;

    if (animateHeight) {
      // Pin the wrapper to its current height so removing `prev` from flow
      // doesn't collapse the panel column. Use the live rendered height
      // (including subpixel) before any layout-affecting changes.
      const startHeight = wrapper.getBoundingClientRect().height;
      wrapper.style.height = `${startHeight}px`;
      // Force a style flush so the pinned height is committed before we
      // swap panel visibility (otherwise the browser may batch them and
      // the wrapper jumps to the next panel's intrinsic height before
      // the transition starts).
      // eslint-disable-next-line no-unused-expressions
      wrapper.offsetHeight;
    }

    // Begin fading prev out and adding next into the flow.
    prev.removeAttribute('data-active');
    next.removeAttribute('hidden');

    // Wait one frame so styles settle before we measure or trigger transitions.
    await this.#nextFrame(signal);
    if (signal.aborted) return;

    next.setAttribute('data-active', '');

    if (!animateHeight) {
      // Mobile: nothing else to animate. Wait for prev's fade-out to finish
      // before fully hiding it so the cross-fade reads naturally.
      await this.#waitForTransition(prev, 'opacity', signal);
      if (signal.aborted) return;
      prev.setAttribute('hidden', '');
      return;
    }

    // Desktop: animate the wrapper from the pinned height to the next panel's
    // natural height, then release the inline height.
    const targetHeight = next.scrollHeight;
    await this.#nextFrame(signal);
    if (signal.aborted) return;
    wrapper.style.height = `${targetHeight}px`;

    await this.#waitForTransition(wrapper, 'height', signal);
    if (signal.aborted) return;

    wrapper.style.height = '';
    prev.setAttribute('hidden', '');
  }

  /**
   * Promise wrapper around `requestAnimationFrame` that respects an
   * AbortSignal so cancelled swaps don't leak rAF callbacks.
   *
   * @param {AbortSignal} signal
   * @returns {Promise<void>}
   */
  #nextFrame(signal) {
    return new Promise((resolve) => {
      if (signal.aborted) {
        resolve();
        return;
      }
      const handle = requestAnimationFrame(() => resolve());
      signal.addEventListener('abort', () => cancelAnimationFrame(handle), { once: true });
    });
  }

  /**
   * Wait for the next `transitionend` on `element` matching `property`, with
   * a safety timeout in case the transition is skipped (zero-distance,
   * `display: none`, etc.).
   *
   * @param {HTMLElement} element
   * @param {string} property
   * @param {AbortSignal} signal
   * @returns {Promise<void>}
   */
  #waitForTransition(element, property, signal) {
    return new Promise((resolve) => {
      if (signal.aborted) {
        resolve();
        return;
      }

      const cleanup = () => {
        element.removeEventListener('transitionend', onEnd);
        clearTimeout(fallback);
      };

      const onEnd = (/** @type {TransitionEvent} */ event) => {
        if (event.target !== element || event.propertyName !== property) return;
        cleanup();
        resolve();
      };

      element.addEventListener('transitionend', onEnd, { signal });

      const fallback = setTimeout(() => {
        cleanup();
        resolve();
      }, 500);

      signal.addEventListener(
        'abort',
        () => {
          cleanup();
          resolve();
        },
        { once: true }
      );
    });
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const target = event.currentTarget;
    if (!(target instanceof HTMLElement)) return;
    const index = this.refs.tabs.indexOf(/** @type {HTMLButtonElement} */ (target));
    if (index !== -1) this.#activate(index, { focus: true });
  };

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    const tabs = this.refs.tabs;
    const current = tabs.indexOf(/** @type {HTMLButtonElement} */ (event.currentTarget));
    if (current === -1) return;

    let next = current;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        next = (current + 1) % tabs.length;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        next = (current - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#activate(next, { focus: true });
  };

  #handleHashChange = () => {
    const index = this.#indexForHash(window.location.hash.replace(/^#/, ''));
    if (index !== -1) this.#activate(index, { updateHash: false });
  };

  /** @type {IntersectionObserver | null} */
  #sectionObserver = null;

  /**
   * Scroll-spy mode. Every panel stays visible and stacked; the menu buttons
   * become in-page jump links and an IntersectionObserver highlights the menu
   * item whose panel is currently in the reading band below the sticky header.
   * The tab-widget roles are swapped for navigation semantics since this is no
   * longer a tab widget. None of the classic swap/animation machinery runs.
   */
  #setupScrollSpy() {
    const { tabs, panels, tablist } = this.refs;
    const signal = this.#getLifecycleSignal();

    // Reveal every panel and expose it as a labelled region.
    for (const panel of panels) {
      panel.removeAttribute('hidden');
      panel.removeAttribute('data-active');
      panel.setAttribute('role', 'region');
    }

    // Menu buttons become jump links: all reachable, no roving tabindex, and
    // the tab role is dropped so AT doesn't announce a broken tab widget.
    for (const tab of tabs) {
      tab.removeAttribute('role');
      tab.removeAttribute('aria-selected');
      tab.setAttribute('tabindex', '0');
      tab.addEventListener('click', this.#handleScrollNav, { signal });
    }

    if (tablist) {
      tablist.removeAttribute('role');
      tablist.removeAttribute('aria-orientation');
      tablist.setAttribute('aria-label', 'Page sections');
    }

    this.#observeSections(signal);
    this.#scrollToInitialHash();
  }

  /**
   * Click a menu item to smooth-scroll to its panel. `scroll-margin-top` on the
   * panel (set in CSS) lands it below the sticky header, so no offset math here.
   *
   * @param {Event} event
   */
  #handleScrollNav = (event) => {
    const tab = event.currentTarget;
    if (!(tab instanceof HTMLElement)) return;
    const panelId = tab.dataset.panelId;
    const panel = panelId ? this.querySelector(`#${CSS.escape(panelId)}`) : null;
    if (!panel) return;

    const behavior = this.#reducedMotionQuery.matches ? 'auto' : 'smooth';
    panel.scrollIntoView({ behavior, block: 'start' });
    history.replaceState(null, '', `#${panelId}`);
    // Set the highlight immediately so it doesn't lag the scroll animation.
    this.#setActiveScrollItem(panelId);
  };

  /**
   * Highlight the menu item whose panel is currently in the reading band. The
   * top of `rootMargin` cuts off just under the sticky header; the bottom
   * (-55%) keeps the active band in the upper ~45% of the viewport so the
   * highlight changes when a new section reaches the reading area.
   *
   * @param {AbortSignal} signal
   */
  #observeSections(signal) {
    const { panels } = this.refs;
    const headerOffset =
      parseInt(getComputedStyle(document.body).getPropertyValue('--header-height'), 10) || 80;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) this.#setActiveScrollItem(visible[0].target.id);
      },
      {
        rootMargin: `-${headerOffset}px 0px -55% 0px`,
        threshold: 0,
      }
    );

    for (const panel of panels) observer.observe(panel);
    this.#sectionObserver = observer;
    signal.addEventListener('abort', () => observer.disconnect(), { once: true });
  }

  /** @param {string} panelId */
  #setActiveScrollItem(panelId) {
    for (const tab of this.refs.tabs) {
      tab.setAttribute('aria-current', tab.dataset.panelId === panelId ? 'true' : 'false');
    }
  }

  /**
   * Honour an inbound hash on load (deep link to a section); otherwise mark the
   * first menu item active.
   */
  #scrollToInitialHash() {
    const matched = this.#indexForHash(window.location.hash.replace(/^#/, ''));
    if (matched === -1) {
      this.#setActiveScrollItem(this.refs.tabs[0]?.dataset.panelId ?? '');
      return;
    }
    const panelId = this.refs.tabs[matched].dataset.panelId ?? '';
    const panel = this.querySelector(`#${CSS.escape(panelId)}`);
    if (!panel) {
      this.#setActiveScrollItem(this.refs.tabs[0]?.dataset.panelId ?? '');
      return;
    }
    requestAnimationFrame(() => panel.scrollIntoView({ behavior: 'auto', block: 'start' }));
    this.#setActiveScrollItem(panelId);
  }
}

if (!customElements.get('tabbed-content')) {
  customElements.define('tabbed-content', TabbedContent);
}
